package com.mrpowergamerbr.puppydough

import web.cssom.ClassName
import web.dom.Element
import web.events.addEventHandler
import web.events.addHandler
import web.html.HTMLDivElement
import web.html.HtmlTagName
import web.pointer.CONTEXT_MENU
import web.pointer.POINTER_CANCEL
import web.pointer.POINTER_DOWN
import web.pointer.POINTER_MOVE
import web.pointer.POINTER_UP
import web.pointer.PointerEvent
import web.cssom.changeEvent
import web.cssom.MediaQuery
import web.cssom.matchMedia
import web.dom.document
import web.navigator.navigator
import web.window.blurEvent
import web.window.window
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.min
import kotlin.math.sqrt

// Where the virtual gamepad forwards GML virtual key codes to, each runner implements this
interface VirtualInputSink {
    fun keyDown(vk: Int)
    fun keyUp(vk: Int)
}

// Tracks which keys are currently held and forwards only edge transitions to the sink, a port of ButterscotchAndroid's VirtualKeyState
// Refcounting handles two pointers mapping to the same key at once, the key stays down until *all* pointers release it
class VirtualKeyState(private val sink: VirtualInputSink) {
    private val refs = mutableMapOf<Int, Int>()

    fun acquire(vk: Int) {
        val newCount = (refs[vk] ?: 0) + 1
        refs[vk] = newCount
        if (newCount == 1) {
            sink.keyDown(vk)
            haptic()
        }
    }

    fun release(vk: Int) {
        val newCount = (refs[vk] ?: return) - 1
        if (newCount <= 0) {
            refs.remove(vk)
            sink.keyUp(vk)
        } else {
            refs[vk] = newCount
        }
    }

    fun transition(oldKeys: Set<Int>, newKeys: Set<Int>) {
        if (oldKeys == newKeys) return
        for (k in oldKeys) if (k !in newKeys) release(k)
        for (k in newKeys) if (k !in oldKeys) acquire(k)
    }

    fun releaseAll() {
        for (vk in refs.keys)
            sink.keyUp(vk)
        refs.clear()
    }
}

/**
 * The default ButterscotchAndroid layouts. [x] and [y] are the element center (0..1 of the overlay), [scale] is relative to the overlay's shorter side
 */
sealed class VirtualGamepadElement(val x: Double, val y: Double, val scale: Double) {
    class Joystick(x: Double, y: Double, scale: Double) : VirtualGamepadElement(x, y, scale)
    class Key(x: Double, y: Double, scale: Double, val vk: Int, val label: String) : VirtualGamepadElement(x, y, scale)
    class ToggleFullscreen(x: Double, y: Double, scale: Double) : VirtualGamepadElement(x, y, scale)
}

object DefaultVirtualGamepadLayouts {
    private const val VK_C = 67
    private const val VK_X = 88
    private const val VK_Z = 90

    val landscape = listOf(
        VirtualGamepadElement.Joystick(0.140625, 0.6875, 0.42),
        VirtualGamepadElement.Key(0.675, 0.8125, 0.22, VK_C, "C"),
        VirtualGamepadElement.Key(0.7875, 0.6875, 0.22, VK_X, "X"),
        VirtualGamepadElement.Key(0.9, 0.5625, 0.22, VK_Z, "Z"),
        VirtualGamepadElement.ToggleFullscreen(0.1, 0.1, 0.10),
    )

    // In portrait the overlay is the strip below the game (ButterscotchAndroid's "Stacked" mode)
    val portrait = listOf(
        VirtualGamepadElement.Joystick(0.25, 0.5625, 0.5050617456436157),
        VirtualGamepadElement.Key(0.75, 0.45, 0.2, VK_C, "C"),
        VirtualGamepadElement.Key(0.625, 0.675, 0.2, VK_X, "X"),
        VirtualGamepadElement.Key(0.875, 0.675, 0.2, VK_Z, "Z"),
        VirtualGamepadElement.ToggleFullscreen(0.1, 0.1, 0.10),
    )
}

object VirtualGamepad {
    private const val VK_LEFT = 37
    private const val VK_UP = 38
    private const val VK_RIGHT = 39
    private const val VK_DOWN = 40

    /**
     * Adds the virtual gamepad to the [gameContainer] if the device has a touch screen
     */
    fun attachIfTouchDevice(gameContainer: Element, sink: VirtualInputSink) {
        if (!matchMedia(MediaQuery("(pointer: coarse)")).matches)
            return

        attach(gameContainer, sink)
    }

    fun attach(gameContainer: Element, sink: VirtualInputSink) {
        val keys = VirtualKeyState(sink)

        // Both layouts are always in the DOM, the CSS hides the one that does not match the current orientation
        gameContainer.el(HtmlTagName.div) {
            className = ClassName("virtual-gamepad landscape")
            suppressLongPress()
            DefaultVirtualGamepadLayouts.landscape.forEach { createElement(it, keys) }
        }

        gameContainer.el(HtmlTagName.div) {
            className = ClassName("virtual-gamepad portrait")
            suppressLongPress()
            DefaultVirtualGamepadLayouts.portrait.forEach { createElement(it, keys) }
        }

        // Avoid stuck keys when the user switches away or rotates the device mid-press
        window.blurEvent.addHandler { keys.releaseAll() }
        matchMedia(MediaQuery("(orientation: portrait)")).changeEvent.addHandler { keys.releaseAll() }
    }

    /**
     * Android Chrome treats a held touch as a long-press, which fires a "contextmenu" event and vibrates the phone, so we cancel it
     */
    private fun HTMLDivElement.suppressLongPress() {
        addEventHandler(PointerEvent.CONTEXT_MENU) { event -> event.preventDefault() }
    }

    private fun HTMLDivElement.createElement(element: VirtualGamepadElement, keys: VirtualKeyState) {
        el(HtmlTagName.div) {
            style.setProperty("--x", element.x.toString())
            style.setProperty("--y", element.y.toString())
            style.setProperty("--scale", element.scale.toString())

            when (element) {
                is VirtualGamepadElement.Key -> setupKey(element, keys)
                is VirtualGamepadElement.Joystick -> setupJoystick(keys)
                is VirtualGamepadElement.ToggleFullscreen -> setupToggleFullscreen()
            }
        }
    }

    private fun HTMLDivElement.setupKey(element: VirtualGamepadElement.Key, keys: VirtualKeyState) {
        className = ClassName("virtual-gamepad-element virtual-gamepad-key")
        textContent = element.label

        var activePointer: Int? = null

        // Hold the press as long as this specific pointer stays down, sliding off the button still keeps the key held
        addEventHandler(PointerEvent.POINTER_DOWN) { event ->
            event.preventDefault()
            if (activePointer != null)
                return@addEventHandler
            activePointer = event.pointerId
            setPointerCapture(event.pointerId)
            classList.add(ClassName("pressed"))
            keys.acquire(element.vk)
        }

        val onRelease: (PointerEvent) -> Unit = { event ->
            if (event.pointerId == activePointer) {
                activePointer = null
                classList.remove(ClassName("pressed"))
                keys.release(element.vk)
            }
        }
        addEventHandler(PointerEvent.POINTER_UP, onRelease)
        addEventHandler(PointerEvent.POINTER_CANCEL, onRelease)
    }

    private fun HTMLDivElement.setupToggleFullscreen() {
        className = ClassName("virtual-gamepad-element virtual-gamepad-key")
        textContent = "F"

        // Hold the press as long as this specific pointer stays down, sliding off the button still keeps the key held
        addEventHandler(PointerEvent.POINTER_DOWN) { event ->
            event.preventDefault()

            haptic()

            if (document.fullscreenElement != null) {
                document.exitFullscreenAsync()
            } else {
                document.documentElement.requestFullscreenAsync()
            }
        }
    }

    /**
     * 8-way directional joystick: map the finger position relative to the joystick centre into an angle, snap that angle to one of 8 sectors, and emit the corresponding arrow key combo.
     *
     * A circular deadzone in the middle suppresses jitter.
     */
    private fun HTMLDivElement.setupJoystick(keys: VirtualKeyState) {
        className = ClassName("virtual-gamepad-element virtual-gamepad-joystick")

        val thumb = el(HtmlTagName.div) {
            className = ClassName("virtual-gamepad-joystick-thumb")
        }

        var activePointer: Int? = null
        var currentKeys = emptySet<Int>()

        fun update(event: PointerEvent) {
            val rect = getBoundingClientRect()
            val radius = min(rect.width, rect.height) / 2
            val deadzone = radius * 0.30
            val dx = event.clientX - (rect.left + rect.width / 2)
            val dy = event.clientY - (rect.top + rect.height / 2)
            val dist = sqrt(dx * dx + dy * dy)

            // Visual thumb position: clamp to base radius so it stays inside the circle
            val clamp = if (dist > radius) radius / dist else 1.0
            thumb.style.transform = "translate(${dx * clamp}px, ${dy * clamp}px)"

            val newKeys = if (dist < deadzone) {
                emptySet()
            } else {
                // Asymmetric 8-way sectors: cardinals are 60 degrees wide, diagonals are only 30 degrees wide
                // Equal 45/45 sectors make "pure down" so narrow that natural thumb-pivot offset usually lands you in a diagonal, which breaks menus like Undertale's name entry
                // Angle is normalized to [0, 360) where 0=right, 90=down, 180=left, 270=up
                val angleDeg = (atan2(dy, dx) * 180.0 / PI + 360.0) % 360.0
                keysForAngle(angleDeg, 30.0)
            }
            keys.transition(currentKeys, newKeys)
            currentKeys = newKeys
        }

        addEventHandler(PointerEvent.POINTER_DOWN) { event ->
            event.preventDefault()
            if (activePointer != null)
                return@addEventHandler
            activePointer = event.pointerId
            setPointerCapture(event.pointerId)
            classList.add(ClassName("holding"))
            update(event)
        }

        addEventHandler(PointerEvent.POINTER_MOVE) { event ->
            if (event.pointerId == activePointer)
                update(event)
        }

        val onRelease: (PointerEvent) -> Unit = { event ->
            if (event.pointerId == activePointer) {
                activePointer = null
                classList.remove(ClassName("holding"))
                thumb.style.transform = ""
                keys.transition(currentKeys, emptySet())
                currentKeys = emptySet()
            }
        }
        addEventHandler(PointerEvent.POINTER_UP, onRelease)
        addEventHandler(PointerEvent.POINTER_CANCEL, onRelease)
    }

    private fun keysForAngle(angleDeg: Double, cardinalHalfWidth: Double): Set<Int> = when {
        angleDeg < cardinalHalfWidth || angleDeg >= 360.0 - cardinalHalfWidth -> setOf(VK_RIGHT)
        angleDeg < 90.0 - cardinalHalfWidth -> setOf(VK_RIGHT, VK_DOWN)
        angleDeg < 90.0 + cardinalHalfWidth -> setOf(VK_DOWN)
        angleDeg < 180.0 - cardinalHalfWidth -> setOf(VK_DOWN, VK_LEFT)
        angleDeg < 180.0 + cardinalHalfWidth -> setOf(VK_LEFT)
        angleDeg < 270.0 - cardinalHalfWidth -> setOf(VK_LEFT, VK_UP)
        angleDeg < 270.0 + cardinalHalfWidth -> setOf(VK_UP)
        else -> setOf(VK_UP, VK_RIGHT)
    }
}

// Short vibration pulse for on-screen control presses, does nothing on browsers without the Vibration API (like Safari)
private fun haptic() {
    if (navigator.asDynamic().vibrate != undefined)
        navigator.vibrate(arrayOf(15))
}
